import { useState, useEffect, useRef, useCallback } from 'react';
import { usePage, Link } from '@inertiajs/react';
import { route } from '../utils/route';
import { Ziggy } from '../ziggy';
import { openChat as openChatUtil } from '../utils/chat';
import PromptBanner from './PromptBanner';

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : Ziggy.url;

interface AuthOrganization {
    id: number;
    name: string;
    slug: string;
    role: string;
}

interface WholesaleTier {
    name: string;
    discount: number;
}

interface AuthUser {
    id: number;
    name: string;
    email: string;
    company: string | null;
    isAdmin: boolean;
    hasAccountReceivable: boolean;
    hasActiveSubscription: boolean;
    wholesaleTier: WholesaleTier | null;
    organization: AuthOrganization | null;
}

interface SharedProps {
    auth: { user: AuthUser | null };
    logoUrl: string;
    cdnUrl: string;
    hasSolutions: boolean;
    defaultCategorySlug: string;
    isImpersonating: boolean;
}

export function Navbar() {
    const { auth, logoUrl, hasSolutions, defaultCategorySlug, isImpersonating } = usePage<SharedProps>().props;
    const user = auth.user;

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [cartCount, setCartCount] = useState(() => {
        try {
            const cached = sessionStorage.getItem('cart_count');
            return cached ? parseInt(cached, 10) || 0 : 0;
        } catch { return 0; }
    });
    const [floatingVisible, setFloatingVisible] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const mobileBackdropRef = useRef<HTMLDivElement>(null);
    const mobilePanelRef = useRef<HTMLDivElement>(null);

    // Route URLs (computed once)
    // Note: admin.* routes are excluded from Ziggy bundle, use hardcoded paths
    const routes = {
        home: route('index'),
        products: route('products.index', { cat: defaultCategorySlug }),
        packs: route('packs.index'),
        solutions: route('solutions.index'),
        cart: route('cart.review'),
        cartContent: route('api.cart.summary'),
        orders: route('orders.index'),
        smartList: route('cart.smart_list'),
        admin: '/admin',
        addresses: route('addresses.index'),
        profile: route('profile'),
        accountReceivable: route('account.index'),
        organization: route('organization.index'),
        login: route('login'),
        logout: route('logout'),
        stopImpersonating: route('stop_impersonating'),
    };

    // Fetch cart count
    const fetchCartCount = useCallback(async () => {
        try {
            const res = await fetch(routes.cartContent, {
                credentials: 'include',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!res.ok) return;
            const data = await res.json();
            const count = data.content ? data.content.length : 0;
            setCartCount(count);
            try { sessionStorage.setItem('cart_count', String(count)); } catch {}
        } catch {
            // silently fail
        }
    }, [routes.cartContent]);

    // Re-fetch on every Inertia page navigation (url changes but component stays mounted)
    const { url } = usePage();
    useEffect(() => {
        fetchCartCount();
    }, [fetchCartCount, url]);

    // Listen for cart updates from other components
    useEffect(() => {
        const handler = (e: Event) => {
            // Read count from event detail if available (avoids redundant fetch)
            const detail = (e as CustomEvent).detail;
            if (detail && typeof detail.count === 'number') {
                setCartCount(detail.count);
                try { sessionStorage.setItem('cart_count', String(detail.count)); } catch {}
            } else {
                // Fallback: fetch from server if no count in event
                fetchCartCount();
            }
        };
        window.addEventListener('cart-updated', handler);
        return () => window.removeEventListener('cart-updated', handler);
    }, [fetchCartCount]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [dropdownOpen]);

    // Imperative open/close — no state, no re-render, pure DOM like the old version
    const openMobileMenu = useCallback(() => {
        const menu = mobileMenuRef.current;
        const backdrop = mobileBackdropRef.current;
        const panel = mobilePanelRef.current;
        if (!menu || !backdrop || !panel) return;
        menu.style.display = 'block';
        menu.style.pointerEvents = 'auto';
        void panel.offsetWidth; // force reflow
        backdrop.style.opacity = '1';
        panel.style.transform = 'translateX(0)';
        document.body.style.overflow = 'hidden';
    }, []);

    const closeMobileMenu = useCallback(() => {
        const menu = mobileMenuRef.current;
        const backdrop = mobileBackdropRef.current;
        const panel = mobilePanelRef.current;
        if (!menu || !backdrop || !panel) return;
        backdrop.style.opacity = '0';
        panel.style.transform = 'translateX(100%)';
        document.body.style.overflow = '';
        setTimeout(() => {
            menu.style.display = 'none';
            menu.style.pointerEvents = 'none';
        }, 300);
    }, []);

    // Floating cart button via IntersectionObserver
    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;
        const observer = new IntersectionObserver(
            ([entry]) => setFloatingVisible(!entry.isIntersecting),
            { threshold: 0, rootMargin: '-70px 0px 0px 0px' }
        );
        observer.observe(nav);
        return () => observer.disconnect();
    }, []);

    const openSupportChat = useCallback(() => {
        openChatUtil(undefined, user?.email, user?.name || user?.company || undefined);
    }, [user]);

    const cartBadgeText = cartCount > 99 ? '99+' : String(cartCount);

    return (
        <>
            {/* Impersonation banner */}
            {isImpersonating && user && (
                <div className="bg-orange-500 text-white text-center text-sm font-medium" style={{ padding: '6px 15px' }}>
                    <i className="fa fa-user-secret" style={{ marginRight: 6 }} />
                    Estás viendo como <strong>{user.name || user.email}</strong>
                    <span className="mx-2">—</span>
                    <a href={routes.stopImpersonating} className="underline hover:no-underline text-white font-semibold">
                        Dejar de impersonar
                    </a>
                </div>
            )}

            {/* Main Navbar */}
            <nav ref={navRef} className="bg-white shadow-md" style={{ borderBottom: '3px solid #1EAA25' }}>
                <div className="mx-auto" style={{ maxWidth: 1310, padding: '0 15px' }}>
                    <div className="flex justify-between items-center" style={{ height: 70 }}>
                        {/* Logo */}
                        <Link href={routes.home} className="flex-shrink-0">
                            <img alt="Abasto Virtual" src={logoUrl} style={{ height: 50 }} />
                        </Link>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center gap-3 text-[15px]">
                            <NavLink href={routes.products} icon="fa-leaf">Productos</NavLink>
                            <NavLink href={routes.packs} icon="fa-box">Packs</NavLink>
                            {hasSolutions && (
                                <NavLink href={routes.solutions} icon="fa-lightbulb">Soluciones</NavLink>
                            )}
                            <NavLink href={routes.cart} icon="fa-shopping-cart">
                                Carrito
                                {cartCount > 0 && (
                                    <span className="ml-1 inline-flex items-center justify-center rounded-full text-white text-[11px] font-semibold"
                                        style={{ background: '#1EAA25', minWidth: 18, height: 18, padding: '0 5px' }}>
                                        {cartBadgeText}
                                    </span>
                                )}
                            </NavLink>

                            {user && (
                                <>
                                    <NavLink href={routes.smartList} icon="fa-wand-magic-sparkles">Armalo por mí</NavLink>
                                    {user.isAdmin && (
                                        <NavLink href={routes.admin} icon="fa-cogs">Admin</NavLink>
                                    )}

                                    {/* User Dropdown */}
                                    <div ref={dropdownRef} className="relative" style={{ marginLeft: 5 }}>
                                        <button
                                            type="button"
                                            onClick={() => setDropdownOpen(!dropdownOpen)}
                                            className="flex items-center py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors bg-transparent border-none cursor-pointer" style={{ gap: 5, padding: '8px 10px' }}
                                        >
                                            <i className="fa fa-user" style={{ position: 'relative', top: -0.5 }} />
                                            <span>{user.name}</span>
                                            {user.wholesaleTier ? (
                                                <WholesaleBadge tier={user.wholesaleTier} />
                                            ) : user.hasActiveSubscription ? (
                                                <span className="inline-flex items-center rounded-full font-semibold text-white" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', fontSize: '8.5px', padding: '1.5px 6px', gap: 3, letterSpacing: '0.3px' }}>
                                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                                    Plus
                                                </span>
                                            ) : null}
                                            {user.company && (
                                                <span className="text-gray-400" style={{ fontSize: '8.75px' }}>[{user.company}]</span>
                                            )}
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {dropdownOpen && (
                                            <div className="absolute right-0 mt-1 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-200" style={{ minWidth: 200 }}>
                                                {user.wholesaleTier && (
                                                    <>
                                                        <div style={{ padding: '4px 14px 8px' }}>
                                                            <div className="flex items-center gap-1.5 font-semibold text-gray-700" style={{ fontSize: 12 }}>
                                                                <CrownIcon size={11} />
                                                                {user.wholesaleTier.name}
                                                            </div>
                                                            <div className="text-gray-500" style={{ fontSize: 11 }}>
                                                                {user.wholesaleTier.discount}% de descuento en todos los productos
                                                            </div>
                                                        </div>
                                                        <hr className="my-1 border-gray-200" />
                                                    </>
                                                )}
                                                <DropdownItem href={routes.orders} icon="fa-list">Mis compras</DropdownItem>
                                                {user.hasAccountReceivable && (
                                                    <DropdownItem href={routes.accountReceivable} icon="fa-credit-card">Cuenta Corriente</DropdownItem>
                                                )}
                                                <hr className="my-1 border-gray-200" />
                                                <DropdownItem href={routes.profile} icon="fa-cog">Perfil</DropdownItem>
                                                <DropdownItem href={routes.addresses} icon="fa-map-marker">Direcciones</DropdownItem>
                                                {user.organization && (
                                                    <DropdownItem href={routes.organization} icon="fa-building">Mi Organización</DropdownItem>
                                                )}
                                                <hr className="my-1 border-gray-200" />
                                                <button onClick={() => { setDropdownOpen(false); openSupportChat(); }} className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors w-full">
                                                    <i className="fa fa-comment" style={{ width: 20, marginRight: 10 }} />
                                                    Soporte
                                                </button>
                                                {isImpersonating ? (
                                                    <a href={routes.stopImpersonating} className="flex items-center px-4 py-2 text-orange-600 hover:bg-orange-50 transition-colors font-medium">
                                                        <i className="fa fa-user-secret" style={{ width: 20, marginRight: 10 }} />
                                                        Dejar de impersonar
                                                    </a>
                                                ) : (
                                                    <DropdownItem href={routes.logout} icon="fa-sign-out">Cerrar sesión</DropdownItem>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {!user && (
                                <div className="flex items-center gap-3">
                                    <button onClick={openSupportChat} className="text-gray-600 hover:text-gray-900 transition-colors bg-transparent border-none cursor-pointer text-sm">
                                        Soporte
                                    </button>
                                    <a href={routes.login}
                                        className="px-5 py-2 text-white rounded-md hover:opacity-90 transition-opacity font-medium flex items-center gap-2"
                                        style={{ background: '#1EAA25' }}>
                                        Ingresar
                                        <i className="fa fa-sign-in" />
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Mobile Cart + Hamburger */}
                        <div className="flex md:hidden items-center gap-2">
                            <SmartLink href={routes.cart} className="rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors relative p-2">
                                <i className="fa fa-shopping-cart text-2xl" />
                                {cartCount > 0 && (
                                    <span className="absolute top-0 right-0 flex items-center justify-center rounded-full text-white text-xs font-bold"
                                        style={{ background: '#1EAA25', minWidth: 18, height: 18, padding: '0 4px' }}>
                                        {cartBadgeText}
                                    </span>
                                )}
                            </SmartLink>
                            <button
                                type="button"
                                onClick={openMobileMenu}
                                className="rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer"
                                style={{ padding: 8 }}
                            >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 28, height: 28, display: 'block' }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay — always in DOM, toggled via display/transform */}
            <div ref={mobileMenuRef} className="fixed inset-0" style={{ zIndex: 99999, display: 'none', pointerEvents: 'none' }}>
                {/* Backdrop */}
                <div
                    ref={mobileBackdropRef}
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{ background: 'rgba(0,0,0,0.3)', opacity: 0 }}
                    onClick={closeMobileMenu}
                />

                {/* Panel - slides from right */}
                <div
                    ref={mobilePanelRef}
                    className="absolute inset-0 bg-gray-50 transition-transform duration-300 ease-out"
                    style={{ transform: 'translateX(100%)' }}
                >
                        {/* Header */}
                        <div className="flex justify-between items-center bg-white shadow-md" style={{ height: 70, borderBottom: '3px solid #1EAA25', padding: '0 10px' }}>
                            <Link href={routes.home} className="flex-shrink-0">
                                <img alt="Abasto Virtual" src={logoUrl} style={{ height: 30 }} />
                            </Link>
                            <button
                                type="button"
                                onClick={closeMobileMenu}
                                className="rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer"
                                style={{ padding: 8 }}
                            >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 28, height: 28, display: 'block' }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Menu Content - all sizes in px to match old Bootstrap (1rem=10px) */}
                        <div className="overflow-y-auto" style={{ height: 'calc(100% - 70px)', padding: 10 }}>
                            <MobileNavLink href={routes.products} icon="fa-leaf">Productos</MobileNavLink>
                            <MobileNavLink href={routes.packs} icon="fa-box">Packs</MobileNavLink>
                            {hasSolutions && (
                                <MobileNavLink href={routes.solutions} icon="fa-lightbulb">Soluciones</MobileNavLink>
                            )}
                            <MobileNavLink href={routes.cart} icon="fa-shopping-cart">Carrito</MobileNavLink>

                            {user && (
                                <>
                                    <MobileNavLink href={routes.smartList} icon="fa-wand-magic-sparkles">Armalo por mí</MobileNavLink>
                                    {user.isAdmin && (
                                        <MobileNavLink href={routes.admin} icon="fa-cogs">Admin</MobileNavLink>
                                    )}

                                    {/* User Section */}
                                    <div className="bg-white rounded-lg shadow-sm" style={{ marginTop: 15, padding: 10 }}>
                                        <div className="flex items-center border-b border-gray-200" style={{ gap: 8, marginBottom: 10, paddingBottom: 10 }}>
                                            <i className="fa fa-user-circle" style={{ color: '#1EAA25', fontSize: 19 }} />
                                            <div>
                                                <div className="font-semibold text-gray-800" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {user.name}
                                                    {user.wholesaleTier ? (
                                                        <WholesaleBadge tier={user.wholesaleTier} />
                                                    ) : user.hasActiveSubscription ? (
                                                        <span className="inline-flex items-center rounded-full font-semibold text-white" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', fontSize: '8.5px', padding: '1.5px 6px', gap: 3, letterSpacing: '0.3px' }}>
                                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                                            Plus
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {user.company && (
                                                    <div className="text-gray-500" style={{ fontSize: 9 }}>{user.company}</div>
                                                )}
                                            </div>
                                        </div>
                                        {user.wholesaleTier && (
                                            <div className="border-b border-gray-200" style={{ paddingBottom: 8, marginBottom: 8 }}>
                                                <div className="flex items-center gap-1.5 font-semibold text-gray-700" style={{ fontSize: 12 }}>
                                                    <CrownIcon size={11} />
                                                    {user.wholesaleTier.name}
                                                </div>
                                                <div className="text-gray-500" style={{ fontSize: 11 }}>
                                                    {user.wholesaleTier.discount}% de descuento en todos los productos
                                                </div>
                                            </div>
                                        )}
                                        <MobileSubLink href={routes.orders} icon="fa-list">Mis compras</MobileSubLink>
                                        {user.hasAccountReceivable && (
                                            <MobileSubLink href={routes.accountReceivable} icon="fa-credit-card">Cuenta Corriente</MobileSubLink>
                                        )}
                                        <MobileSubLink href={routes.profile} icon="fa-cog">Perfil</MobileSubLink>
                                        <MobileSubLink href={routes.addresses} icon="fa-map-marker">Direcciones</MobileSubLink>
                                        {user.organization && (
                                            <MobileSubLink href={routes.organization} icon="fa-building">Mi Organización</MobileSubLink>
                                        )}
                                        <button onClick={() => { closeMobileMenu(); openSupportChat(); }} className="flex items-center text-gray-700 border-b border-gray-100 hover:text-green-600 transition-colors w-full bg-transparent border-none cursor-pointer" style={{ gap: 10, padding: '8px 0' }}>
                                            <i className="fa fa-comment text-gray-500" style={{ fontSize: 11, width: 15 }} />
                                            <span>Soporte</span>
                                        </button>
                                        {isImpersonating ? (
                                            <a href={routes.stopImpersonating} className="flex items-center text-orange-600 hover:text-orange-700 font-medium transition-colors" style={{ gap: 10, padding: '8px 0' }}>
                                                <i className="fa fa-user-secret" style={{ fontSize: 11, width: 15 }} />
                                                <span>Dejar de impersonar</span>
                                            </a>
                                        ) : (
                                            <button onClick={() => { closeMobileMenu(); setShowLogoutConfirm(true); }} className="flex items-center text-red-600 hover:text-red-700 transition-colors w-full bg-transparent border-none cursor-pointer" style={{ gap: 10, padding: '8px 0' }}>
                                                <i className="fa fa-sign-out" style={{ fontSize: 11, width: 15 }} />
                                                <span>Cerrar sesión</span>
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-center gap-4 text-sm text-gray-500" style={{ marginTop: 20 }}>
                                        <button onClick={() => { closeMobileMenu(); openSupportChat(); }} className="hover:text-gray-700 transition-colors">
                                            Soporte
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <Link href="/politicas" className="hover:text-gray-700 transition-colors">
                                            Políticas
                                        </Link>
                                    </div>
                                </>
                            )}

                            {!user && (
                                <div style={{ padding: 20 }}>
                                    <a href={routes.login}
                                        className="flex items-center justify-center font-semibold text-white rounded-lg shadow-md hover:opacity-90 transition-opacity"
                                        style={{ background: '#1EAA25', gap: 8, padding: 10, fontSize: 11 }}>
                                        <i className="fa fa-sign-in" />
                                        <span>Ingresar</span>
                                    </a>
                                    <div className="flex items-center justify-center gap-4 text-sm text-gray-500" style={{ marginTop: 20 }}>
                                        <button
                                            type="button"
                                            onClick={() => { closeMobileMenu(); openSupportChat(); }}
                                            className="hover:text-gray-700 transition-colors bg-transparent border-none cursor-pointer"
                                        >
                                            Contactános
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <Link href="/politicas" className="hover:text-gray-700 transition-colors">
                                            Políticas
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

            {/* Floating Cart Button (desktop only) */}
            {cartCount > 0 && floatingVisible && (
                <SmartLink href={routes.cart}
                    className="hidden md:flex fixed items-center justify-center rounded-full text-white text-2xl shadow-lg hover:opacity-90 transition-all z-[9999]"
                    style={{ bottom: 100, right: 24, width: 56, height: 56, background: '#1EAA25' }}>
                    <i className="fa fa-shopping-cart" />
                    <span className="absolute flex items-center justify-center rounded-full text-white text-xs font-bold shadow"
                        style={{ top: -4, right: -4, minWidth: 22, height: 22, background: '#ef4444', padding: '0 6px' }}>
                        {cartBadgeText}
                    </span>
                </SmartLink>
            )}

            {showLogoutConfirm && (
                <PromptBanner
                    icon={<div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center"><i className="fa fa-sign-out text-red-600 text-xl" /></div>}
                    title="Cerrar sesión"
                    description="¿Estás seguro de que querés cerrar sesión?"
                    dismissLabel="Cancelar"
                    actionLabel="Cerrar sesión"
                    onClose={() => setShowLogoutConfirm(false)}
                    actionHref={routes.logout}
                />
            )}
        </>
    );
}

/* --- Wholesale Tier Badge --- */

const TIER_STYLES: Record<string, { bg: string; label: string }> = {
    'Mayorista': { bg: 'linear-gradient(135deg, #22c55e, #16a34a)', label: 'Mayorista' },
    'Mayorista Plus': { bg: 'linear-gradient(135deg, #6366f1, #4f46e5)', label: 'Mayorista Plus' },
    'Mayorista Premium': { bg: 'linear-gradient(135deg, #f59e0b, #b45309)', label: 'Premium' },
};

function CrownIcon({ size = 9 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 20h20v2H2v-2zm1-7l4.5 3.5L12 5l4.5 11.5L21 13l-1.5 7h-15L3 13z"/>
        </svg>
    );
}

function WholesaleBadge({ tier }: { tier: WholesaleTier }) {
    const style = TIER_STYLES[tier.name] || TIER_STYLES['Mayorista'];
    return (
        <span
            className="inline-flex items-center rounded-full font-semibold text-white"
            style={{ background: style.bg, fontSize: '8.5px', padding: '1.5px 6px', gap: 3, letterSpacing: '0.3px' }}
        >
            <CrownIcon />
            {style.label}
        </span>
    );
}

/* --- Sub-components --- */

function useCurrentPath(): string {
    const { url } = usePage();
    try { return new URL(url, ORIGIN).pathname; } catch { return url; }
}

// Paths that have their own nav link and should NOT activate their parent
const EXACT_MATCH_PATHS = ['/carrito'];

function isPathActive(currentPath: string, href: string): boolean {
    const linkPath = new URL(href, ORIGIN).pathname;
    if (linkPath === '/') return currentPath === '/';
    if (EXACT_MATCH_PATHS.includes(linkPath)) return currentPath === linkPath;
    return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
}

function NavLink({ href, icon, children }: { href: string; icon: string; children: React.ReactNode }) {
    const currentPath = useCurrentPath();
    const active = isPathActive(currentPath, href);
    return (
        <SmartLink href={href} className={`px-2.5 py-2 rounded-md transition-colors flex items-center ${active ? 'text-green-700 bg-green-50 font-medium' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`} style={{ gap: 5 }}>
            <i className={`fa ${icon}`} style={{ position: 'relative', top: -0.5 }} />
            <span>{children}</span>
        </SmartLink>
    );
}

// Routes that use Inertia (SPA navigation). All others get a full page load.
const INERTIA_PATHS = ['/', '/perfil', '/direcciones', '/cuenta-corriente', '/compras', '/packs', '/receta', '/soluciones', '/carrito', '/carrito/lista-inteligente', '/productos', '/producto', '/pedido', '/verificado', '/d', '/p', '/politicas', '/politica-de-privacidad', '/politica-de-devolucion', '/terminos-de-servicio', '/bienvenido', '/gastronomicos', '/verdulerias', '/educacion', '/organizaciones-sociales', '/barrios-privados', '/seres-queridos', '/por-que-elegirnos', '/precios-mayoristas', '/mi-empresa', '/mi-organizacion', '/invitacion', '/soporte', '/regalar'];

function SmartLink({ href, className, style, children }: {
    href: string; className?: string; style?: React.CSSProperties; children: React.ReactNode;
}) {
    const isInertia = INERTIA_PATHS.some(p => {
        const path = new URL(href, ORIGIN).pathname;
        return path === p || path.startsWith(p + '/');
    });
    if (isInertia) {
        return <Link href={href} className={className} style={style}>{children}</Link>;
    }
    return <a href={href} className={className} style={style}>{children}</a>;
}

function DropdownItem({ href, icon, children }: { href: string; icon: string; children: React.ReactNode }) {
    return (
        <SmartLink href={href} className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors">
            <i className={`fa ${icon}`} style={{ width: 20, marginRight: 10 }} />
            {children}
        </SmartLink>
    );
}

function MobileNavLink({ href, icon, children }: { href: string; icon: string; children: React.ReactNode }) {
    const currentPath = useCurrentPath();
    const active = isPathActive(currentPath, href);
    return (
        <SmartLink href={href} className={`flex items-center font-medium rounded-lg shadow-sm transition-colors ${active ? 'bg-green-50 text-green-700 border-l-4 border-green-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            style={{ gap: 10, padding: 10, fontSize: 17, lineHeight: '32.5px', marginBottom: 5, borderRadius: 5 }}>
            <i className={`fa ${icon}`} style={{ color: '#1EAA25', fontSize: 15, width: 20 }} />
            <span>{children}</span>
        </SmartLink>
    );
}

function MobileSubLink({ href, icon, children }: { href: string; icon: string; children: React.ReactNode }) {
    return (
        <SmartLink href={href} className="flex items-center text-gray-700 border-b border-gray-100 hover:text-green-600 transition-colors"
            style={{ gap: 10, padding: '8px 0' }}>
            <i className={`fa ${icon} text-gray-500`} style={{ fontSize: 11, width: 15 }} />
            <span>{children}</span>
        </SmartLink>
    );
}
